// FinancialReport — the Reports tab's financial performance view: what the
// contract has been billed, what has been approved, how long approval takes,
// and how much of the contract value that consumes.
//
// The view holds no derivation. Every figure arrives from the server, which
// splits the model on purpose: only the monthly rows and the average approval
// time follow the selected window, while the value totals and the
// awaiting-approval queue stay contract-to-date so they agree with the
// cumulative position rendered beside them. The strip says which is which,
// because a reader comparing a contract-to-date total against a twelve-month
// chart has no other way to tell.
//
// "Approved", never "Paid". Invoice statuses stop at approved and the system
// holds no settlement record, so a "Paid" label would be a claim the data
// cannot support. A test asserts the string never reaches the rendered view.
//
// Failure is whole-view by design, like the other focused reports: one data
// domain backs it, and a zeroed chart would read as a contract nobody has
// billed against.

import { Alert, LoadingSpinner } from "../../common";
import MetricCard from "../tracking/deliverable/MetricCard";
import ReportSection from "./ReportSection";
import CumulativePositionBar from "./CumulativePositionBar";
import InvoicedColumns from "./charts/InvoicedColumns";
import { TD_CLASS, TD_NUMERIC_CLASS, Th } from "./reportTable";
import { countTone, formatReportMonth } from "./reportFormat";
import { formatAud2dp } from "../../../utils/formatCurrency";
import { useReportFinancial } from "../../../hooks/useContractReport";
import type {
  FinancialMetrics,
  FinancialMonthRow,
  FinancialReportModel,
  ReportRange,
} from "../../../types/report";

export interface FinancialReportProps {
  contractId: string;
  /** Owned by the Reports tab, which renders the range chips for every
   *  focused view. */
  range: ReportRange;
}

export default function FinancialReport({
  contractId,
  range,
}: FinancialReportProps) {
  const { data, isPending, error } = useReportFinancial(contractId, range);

  if (isPending) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="error">
        The financial performance report couldn&apos;t be loaded. Refresh to
        try again.
      </Alert>
    );
  }

  return <FinancialBody model={data} />;
}

function days(count: number): string {
  return `${count} ${count === 1 ? "day" : "days"}`;
}

/** The queue line: how many are waiting and how long the longest has been.
 *
 *  "Awaiting now" because this tile is the odd one out on the strip: the two
 *  value totals are contract-to-date and the turnaround follows the selected
 *  window, while the queue is a position at this moment. Without the cue a
 *  reader takes it for a figure about the window they chose.
 *
 *  A contract with nothing waiting says so rather than showing a nought. */
function queueSubtitle(metrics: FinancialMetrics): string {
  if (metrics.inReviewCount === 0) return "No invoices awaiting review";
  const count = `${metrics.inReviewCount} ${metrics.inReviewCount === 1 ? "invoice" : "invoices"} awaiting now`;
  if (metrics.oldestInReviewDays === null) return count;
  return `${count} · oldest waiting ${days(metrics.oldestInReviewDays)}`;
}

function FinancialBody({ model }: { model: FinancialReportModel }) {
  const { metrics, monthly, cumulative, gstBasis } = model;
  const nothingBilled = monthly.every((row) => row.invoiceCount === 0);

  return (
    <div className="space-y-6">
      <div>
        <p
          data-testid="financial-report-gst-basis"
          className="text-caption text-slate-500 dark:text-slate-400"
        >
          All values {gstBasis}.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            testId="financial-report-invoiced"
            label="Invoiced to date"
            value={formatAud2dp(metrics.invoicedToDate)}
            subtitle="Contract to date"
          />
          <MetricCard
            testId="financial-report-approved"
            label="Approved to date"
            value={formatAud2dp(metrics.approvedToDate)}
            subtitle="Contract to date"
          />
          <MetricCard
            testId="financial-report-approval-time"
            label="Average approval time"
            value={
              metrics.avgApprovalDays === null
                ? "—"
                : days(metrics.avgApprovalDays)
            }
            subtitle="Invoice date to approval, in this window"
          />
          <MetricCard
            testId="financial-report-in-review"
            label="Invoices awaiting review"
            value={formatAud2dp(metrics.inReviewValue)}
            valueTone={countTone(metrics.inReviewCount, "warning")}
            subtitle={queueSubtitle(metrics)}
          />
        </div>
      </div>

      <ReportSection id="financial-months" title="Invoiced by month">
        {nothingBilled ? (
          <p
            data-testid="financial-report-empty"
            className="text-body-sm text-slate-500 dark:text-slate-400"
          >
            No invoices are dated in this window. The totals above are
            contract-to-date, so a contract billed earlier still shows a
            position below.
          </p>
        ) : (
          <>
            <InvoicedColumns monthly={monthly} gstBasis={gstBasis} />
            <div className="mt-4">
              <MonthlyDetailTable monthly={monthly} gstBasis={gstBasis} />
            </div>
          </>
        )}
      </ReportSection>

      <ReportSection
        id="financial-position"
        title="Contract value consumed by invoices"
      >
        <CumulativePositionBar
          cumulative={cumulative}
          gstBasis={gstBasis}
          barTestId="financial-report-consumption"
          paceTestId="financial-report-pace"
          overrunTestId="financial-report-overrun"
        />
      </ReportSection>
    </div>
  );
}

/** The numeric read-out the chart points at. Every month in the window has a
 *  row, so a month nobody billed in is visible as a nought rather than as a
 *  missing line. */
function MonthlyDetailTable({
  monthly,
  gstBasis,
}: {
  monthly: FinancialMonthRow[];
  gstBasis: FinancialReportModel["gstBasis"];
}) {
  return (
    <div
      role="group"
      aria-label="Monthly invoicing detail"
      tabIndex={0}
      data-testid="financial-report-months-table"
      className="relative overflow-x-auto focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
    >
      <table className="w-full">
        <caption className="sr-only">Monthly detail</caption>
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-700">
            <Th>Month</Th>
            <Th numeric>Invoices</Th>
            <Th numeric>Invoiced ({gstBasis})</Th>
            <Th numeric>Approved ({gstBasis})</Th>
            <Th numeric>Avg approval days</Th>
          </tr>
        </thead>
        <tbody>
          {monthly.map((row) => (
            <tr
              key={row.month}
              className="border-b border-slate-100 last:border-0 dark:border-slate-800"
            >
              <td className={`${TD_CLASS} whitespace-nowrap`}>
                {formatReportMonth(row.month)}
              </td>
              <td className={TD_NUMERIC_CLASS}>{row.invoiceCount}</td>
              <td className={TD_NUMERIC_CLASS}>
                {formatAud2dp(row.invoicedTotal)}
              </td>
              <td className={TD_NUMERIC_CLASS}>
                {formatAud2dp(row.approvedTotal)}
              </td>
              <td className={TD_NUMERIC_CLASS}>
                {row.avgApprovalDays === null ? "—" : row.avgApprovalDays}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
